import { Button, Grid, Link, makeStyles, Popover, Tooltip } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import copy from 'copy-to-clipboard';
import {
  enqueueSnackbar,
  SNACKBAR_VARIANTS,
} from '../../../redux_store';
import cx from 'classnames';
import Typography from '@material-ui/core/Typography';
import { INFO_PANE_SIZES } from '../constants';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

let timeout;

const popupPosition = {
  vertical: 'center',
  horizontal: 'center',
};

const useStyles = makeStyles(() => ({
  text: {
    fontSize: 14,
    borderBottom: '1px solid rgba(213, 213, 213, 1)',
    justifyContent: 'space-between',
    padding: '12px 0px',
    gap: 0,
    color: '#484554',
    flexWrap: 'nowrap',
    alignItems: 'flex-start',
  },
  textWrapper: {
    marginRight: 16,
    flexShrink: 0,
    flexGrow: 0,
  },
  copyButton: {
    transition: '.3s',
    maxWidth: INFO_PANE_SIZES.full_width,
    padding: '0 24px',

    '&:hover': {
      backgroundColor: 'rgba(72, 69, 84, 0.04)',
      cursor: 'pointer',
      transition: '.1s',
    },
  },
  copyButtonWithoutValue: {
    '&:hover': {
      backgroundColor: '#ffffff',
      cursor: 'not-allowed',
      transition: '.1s',
    },
  },
  copyButtonWithAction: {
    '&:has(.actionChild:hover)': {
      backgroundColor: 'transparent',
    },
  },
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    flex: '1 1 auto',
    flexGrow: 1,
    flexShrink: 100,
    minWidth: 0,
  },
  infoContainer: {
    alignItems: 'center',
    flexWrap: 'nowrap',
    maxWidth: '100%',
    flexGrow: 0,
    flexShrink: 100,
    minWidth: 0,
    display: 'flex',
  },
  actionChild: {
    textDecoration: 'underline',
    color: '#5B65DC',
    transition: '.3s',
    textAlign: 'right',
    cursor: 'pointer',

    '&:hover': {
      transition: '.3s',
      color: '#0073b0',
    },
  },
  popover: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: 12,
  },
  handlerNote: {
    fontSize: 11,
    color: '#8E8E8E',
    maxWidth: 300,
    lineHeight: 1.4,
  },
  handlerLink: {
    fontSize: 11,
    color: '#5B65DC',
    cursor: 'pointer',
  },
  infoIcon: {
    width: 16,
    paddingLeft: 4,
    color: '#868686',
    height: 16,
    marginTop: 1,
    flexGrow: 0,
    flexShrink: 0,
    minWidth: 20,
    minHeight: 16,
  },
  cursor: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#ffffff',
      cursor: 'pointer',
    },
  },
  columnStyle: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
}));

const EMAIL_HANDLER_GUIDE_URL = 'https://support.google.com/chrome/thread/57026170/how-to-add-gmail-as-default-mailto-handler?hl=en';

const InfoRow = ({
  title,
  info,
  dataAction,
  dataActionSecondary,
  dataActionTooltipTitle,
  customCopyValue,
}) => {
  const classes = useStyles();
  const { en: { infoPane, notifications } } = useSelector((state) => state.i18n);
  const { isInfoPaneExpanded } = useSelector((state) => state.infoPane);
  const [isHovered, setIsHovered] = useState(false);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const [isNewData, setIsNewData] = useState(false);
  const didMountRef = useRef(false);

  const _stopPropagation = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleClick = (event) => {
    _stopPropagation(event);
    setTooltipIsOpen(false);
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    _stopPropagation(event);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const copyToClipBoard = async copyMe => {
    try {
      await copy(customCopyValue ?? copyMe);
      dispatch(
        enqueueSnackbar({
          message: notifications.dataCopied,
          options: {
            variant: SNACKBAR_VARIANTS.success,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          },
        }),
      );
    } catch (err) {
      enqueueSnackbar({
        message: notifications.dataNotCopied,
        options: {
          variant: SNACKBAR_VARIANTS.error,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          },
        },
      });
    }
  };

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }

    setIsNewData(true);
    setTimeout(() => setIsNewData(false), 1500);
  }, [info]);


  let whenMouseMoves = () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => setTooltipIsOpen(false), 3000);
  };

  let whenMouseLeave = () => {
    clearTimeout(timeout);
    setTooltipIsOpen(false);
  };

  const hasData = useMemo(
    () => info && info.toString().replaceAll(' ', '').replaceAll('-', '').length,
    [info],
  );

  const hasAction = useMemo(
    () => !!(dataAction && hasData),
    [hasData, dataAction],
  );
  const infoText = useMemo(
    () => {
      let value = info;

      if (Array.isArray(value)) {
        return value.map(e => <div style={{ maxWidth: '100%' }} className={classes.info}>{e}</div>);
      }

      if (typeof value === 'number' && !isNaN(value)) {
        value = value.toLocaleString();
      }

      if (title.includes('Rate')) {
        value = (value * 100).toFixed(1) + '%';
      }

      return <div className={classes.info}>
        {value || '-'}
      </div>;
    },
    [info],
  );

  return <Grid item
    className={cx(classes.copyButton, {
      [classes.copyButtonWithAction]: hasAction,
      [classes.copyButtonWithoutValue]: !hasData,
      [classes.cursor]: !isInfoPaneExpanded,
    })}
    onMouseMove={isInfoPaneExpanded ? whenMouseMoves : null}
    onMouseLeave={isInfoPaneExpanded ? whenMouseLeave : null}
    onClick={isInfoPaneExpanded && hasData ? () => copyToClipBoard(info) : null}>
    <Tooltip
      open={tooltipIsOpen}
      onOpen={() => setTooltipIsOpen(true)}
      onClose={() => setTooltipIsOpen(false)}
      disableFocusListener={isHovered || open || !hasData}
      disableHoverListener={isHovered || open || !hasData}
      disableTouchListener={isHovered || open || !hasData}
      enterNextDelay={800}
      title={isHovered ? dataActionTooltipTitle : infoPane.clickToCopyToClipboard}
    >
      <div>
        <Grid item container className={classes.text}
          style={{
            transition: 'all .3s',
            backgroundColor: isNewData ? 'rgba(255, 230, 0, 0.15)' : null,
          }}
        >
          <Grid item className={classes.textWrapper}>
            <b>{title + ':'}</b>
          </Grid>
          <div className={cx(classes.infoContainer, {
            [classes.actionChild]: hasAction,
            [classes.columnStyle]: Array.isArray(info),
            'actionChild': hasAction,
          })}
            onClick={hasAction ? handleClick : null}
            onMouseEnter={hasAction ? () => setIsHovered(true) : null}
            onMouseLeave={hasAction ? () => setIsHovered(false) : null}
          >
            {infoText}
            {hasAction && <MailOutlineIcon className={classes.infoIcon} />}
          </div>

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={popupPosition}
            transformOrigin={popupPosition}
            style={{ zIndex: 100000 }}
          >
            <div onClick={_stopPropagation} className={classes.popover}>
              <Typography variant={'h6'}>
                {infoPane.emailPatient}
              </Typography>
              <Typography variant={'body'} style={{ maxWidth: 300 }}>
                {infoPane.openEmailApp}
              </Typography>
              <Button
                variant={'outlined'}
                color={'primary'}
                onClick={(e) => {
                  handleClose(e);
                  dataAction(e);
                }}
              >
                {infoPane.emailPatientWithApp}
                <MailOutlineIcon fontSize={'small'} style={{ marginLeft: 8 }} />
              </Button>
              {dataActionSecondary && (
                <Button
                  variant={'outlined'}
                  color={'primary'}
                  onClick={(e) => {
                    handleClose(e);
                    dataActionSecondary(e);
                  }}
                >
                  {infoPane.emailPatientWithOutlook}
                  <OpenInNewIcon fontSize={'small'} style={{ marginLeft: 8 }} />
                </Button>
              )}
              <Typography className={classes.handlerNote}>
                {infoPane.emailHandlerNote}{' '}
                <Link
                  className={classes.handlerLink}
                  component="span"
                  onClick={() => window.open(EMAIL_HANDLER_GUIDE_URL, '_blank')}
                >
                  {infoPane.emailHandlerLink}
                </Link>
              </Typography>
            </div>
          </Popover>
        </Grid>
      </div>
    </Tooltip>
  </Grid>;
};

export default InfoRow;
